import { ApiError, isErrorDocument, NetworkError } from './errors'
import type { Document, NewResource, ResourceUpdate } from './types'

/**
 * The exact media type the API requires. Any other Accept value fails every
 * endpoint with 415, so this is not negotiable and not configurable.
 */
export const JSON_API_MEDIA_TYPE = 'application/vnd.api+json'

export const DEFAULT_API_BASE = 'https://api.codeverse.com'

let apiBase = DEFAULT_API_BASE

/** Point the client at a different origin (dev mock, staging). */
export function setApiBase(base: string): void {
  apiBase = base.replace(/\/+$/, '')
}

export function getApiBase(): string {
  return apiBase
}

export interface AuthContext {
  accessToken: string
  /** The user being acted on behalf of — normally the signed-in user. */
  userId: string
  /**
   * Optional. When omitted the API falls back to the user's first membership
   * organization, which is the correct behaviour for password logins.
   */
  organizationId?: string
}

export interface ApiFetchOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  auth?: AuthContext | null
  signal?: AbortSignal
}

/** What a resource module's functions take: who is asking, and a way to stop asking. */
export type RequestOptions = Pick<ApiFetchOptions, 'auth' | 'signal'>

/**
 * Single entry point for every API call.
 *
 * Every non-2xx response is turned into an ApiError carrying the JSON:API
 * error code, and any transport-level failure into a NetworkError, so callers
 * only ever deal with those two.
 */
export async function apiFetch<T>(path: string, options: ApiFetchOptions = {}): Promise<T> {
  const { method = 'GET', body, auth, signal } = options

  const headers = new Headers({ Accept: JSON_API_MEDIA_TYPE })
  if (body !== undefined) headers.set('Content-Type', JSON_API_MEDIA_TYPE)
  if (auth) {
    headers.set('Authorization', `Bearer ${auth.accessToken}`)
    headers.set('Current-User-Id', auth.userId)
    if (auth.organizationId) headers.set('Current-Organization-Id', auth.organizationId)
  }

  let response: Response
  try {
    response = await fetch(`${apiBase}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    })
  } catch (cause) {
    // fetch() rejects for offline, DNS failure, and — importantly — a failed
    // CORS preflight, which is the most likely first-run failure here.
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause
    throw new NetworkError(cause)
  }

  if (!response.ok) throw new ApiError(response.status, await readErrors(response))

  if (response.status === 204) return undefined as T
  return (await response.json()) as T
}

/**
 * POST a new resource. `resource.type` must match the collection in `path`
 * or the API answers 409 request_conflict.
 */
export function apiCreate<T>(
  path: string,
  resource: NewResource,
  options: Omit<ApiFetchOptions, 'method' | 'body'> = {},
): Promise<T> {
  const body: Document<NewResource> = { data: resource }
  return apiFetch<T>(path, { ...options, method: 'POST', body })
}

/**
 * PUT changes to an existing resource. Unlike a create, the id travels inside
 * `data` as well as in the path — the API rejects an update document without it.
 */
export function apiUpdate<T>(
  path: string,
  resource: ResourceUpdate,
  options: Omit<ApiFetchOptions, 'method' | 'body'> = {},
): Promise<T> {
  const body: Document<ResourceUpdate> = { data: resource }
  return apiFetch<T>(path, { ...options, method: 'PUT', body })
}

async function readErrors(response: Response) {
  try {
    const payload: unknown = await response.json()
    if (isErrorDocument(payload)) return payload.errors
  } catch {
    // Error bodies are not guaranteed to be JSON — a gateway 502 usually isn't.
  }
  return [{ status: String(response.status), detail: response.statusText }]
}
